import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  User,
  Phone,
  CheckCircle2,
  ArrowRight,
  Clock,
  FileText,
  MessageCircle,
  ImagePlus,
  X,
} from 'lucide-react';
import { useCMS } from '../hooks/useCMS';
import { CMSLoader } from '../components/CMSLoader';
import type { BookingForm } from '../types';

const BookingPage: React.FC = () => {
  const { data: cms, loading } = useCMS();

  // 手機：用隱藏 iframe 觸發 whatsapp:// deeplink 喚醒 App，
  // 當前分頁永遠停留在「預約已確認」版面；App 沒開也不導走頁面。
  // Android 用 intent://（兩個 App 都裝時系統會彈出選擇器）；
  // iOS 用 whatsapp://（系統彈出確認對話框）。
  const openWhatsApp = (data: { whatsappUrl: string; whatsappPhone?: string; whatsappText?: string }) => {
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if ((isAndroid || isIOS) && data.whatsappPhone && data.whatsappText) {
      const text = encodeURIComponent(data.whatsappText);
      const link = isAndroid
        ? `intent://send?phone=${data.whatsappPhone}&text=${text}#Intent;scheme=whatsapp;end`
        : `whatsapp://send?phone=${data.whatsappPhone}&text=${text}`;
      setTimeout(() => {
        if (isIOS) {
          window.location.href = link; // iOS 無 App 時只彈系統提示，不會導走頁面
        } else {
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = link;
          document.body.appendChild(iframe);
          setTimeout(() => iframe.remove(), 3000);
        }
      }, 500);
    } else {
      setTimeout(() => {
        window.open(data.whatsappUrl, '_blank');
      }, 1500);
    }
  };

  const [form, setForm] = useState<BookingForm>({
    contactPerson: '',
    phone: '',
    email: '',
    relocationType: '',
    preferredDate: '',
    preferredTime: '',
    notes: '',
  });
  const [images, setImages] = useState<File[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waData, setWaData] = useState<{
    whatsappUrl: string;
    whatsappPhone?: string;
    whatsappText?: string;
  } | null>(null);

  // 提交成功後捲回確認版面最頂（從 WhatsApp 返回時不會停在頁尾）
  useEffect(() => {
    if (submitted) window.scrollTo(0, 0);
  }, [submitted]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setImages((prev) => [...prev, ...files].slice(0, 5));
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  // 上傳前壓縮：最長邊 1600px、JPEG 82% —— 手機相片 3-5MB → 約 200KB
  const compressImage = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 1600;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas not supported'));
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('壓縮失敗'))),
          'image/jpeg',
          0.82
        );
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => reject(new Error('圖片讀取失敗'));
      img.src = URL.createObjectURL(file);
    });
  };

  const uploadImages = async (): Promise<string[]> => {
    const urls: string[] = [];
    for (const file of images) {
      const blob = await compressImage(file);
      const fd = new FormData();
      fd.append('file', new File([blob], 'photo.jpg', { type: 'image/jpeg' }));
      const res = await fetch('/api/upload', { method: 'POST', body: fd });
      if (!res.ok) throw new Error('圖片上傳失敗，請稍後再試');
      const data = await res.json();
      urls.push(data.shortUrl || data.url);
    }
    return urls;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const imageUrls = images.length > 0 ? await uploadImages() : [];
      const res = await fetch('/api/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, imageUrls }),
      });
      if (!res.ok) throw new Error('提交失敗，請稍後再試');
      const data = await res.json();
      setWaData(data);
      setSubmitted(true);
      openWhatsApp(data);    } catch (err) {
      setError(err instanceof Error ? err.message : '提交失敗');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  if (loading || !cms) return <CMSLoader />;

  const relocationTypes = ['醫療設備搬遷', '美容院設備搬遷', '診所搬遷', '其他'];

  const timeSlots = [
    '09:00 - 11:00',
    '11:00 - 13:00',
    '14:00 - 16:00',
    '16:00 - 18:00',
    '18:00 後（夜間服務）',
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-cream-50 pt-32 pb-20 px-6 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 border border-amber-300 text-amber-800 text-xs font-bold tracking-wider uppercase rounded-full mb-8">
            最後一步
          </div>
          <div className="w-20 h-20 mx-auto mb-8 bg-[#25D366]/10 border-2 border-[#25D366] flex items-center justify-center">
            <MessageCircle className="text-[#25D366]" size={36} />
          </div>
          <h2 className="text-3xl font-serif text-navy-900 mb-4">請發送 WhatsApp 訊息完成預約</h2>
          <p className="text-navy-600/80 text-lg mb-8 leading-relaxed">
            您的預約資料已準備好。請在 WhatsApp 中按「發送」傳送預約訊息——<strong className="text-navy-900">我們收到訊息後，預約才算正式完成</strong>，並於 24 小時內致電確認上門評估安排。
          </p>

          {waData && (
            <button
              onClick={() => openWhatsApp(waData)}
              className="inline-flex items-center justify-center gap-2 w-full px-8 py-4 bg-[#25D366] hover:bg-[#20BD5A] text-white font-bold text-base tracking-wider rounded-full transition-colors shadow-lg shadow-green-500/25 mb-8"
            >
              <MessageCircle size={22} />
              發送 WhatsApp 預約訊息
            </button>
          )}

          <div className="bg-white border border-cream-300 p-6 text-left space-y-3">
            <p className="text-xs text-navy-600/50 uppercase tracking-wider mb-2">預約資料摘要</p>
            <div className="flex justify-between text-sm">
              <span className="text-navy-600/60">聯絡人</span>
              <span className="text-navy-900 font-medium">{form.contactPerson}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-navy-600/60">搬遷類型</span>
              <span className="text-navy-900 font-medium">{form.relocationType}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-navy-600/60">期望日期</span>
              <span className="text-navy-900 font-medium">{form.preferredDate}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-navy-600/60">期望時段</span>
              <span className="text-navy-900 font-medium">{form.preferredTime}</span>
            </div>
          </div>
          <p className="text-xs text-navy-600/50 mt-4">
            如 WhatsApp 未自動開啟，請點擊上方綠色按鈕
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div>
      <section className="bg-navy-900 pt-32 pb-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-6"
          >
            <div className="gold-divider" />
            <span className="text-gold-400 text-xs tracking-[0.3em] uppercase">Book Assessment</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-5xl text-white font-serif mb-4"
          >
            預約免費上門評估
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-white/60 text-lg max-w-2xl"
          >
            填寫以下表單，我們的資深顧問將親臨貴公司進行專業評估，為您制定最佳搬遷方案。
          </motion.p>
        </div>
      </section>

      <section className="section-padding bg-cream-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white border border-cream-300 p-8 md:p-12">
                <h3 className="text-2xl font-serif text-navy-900 mb-2">填寫評估資料</h3>
                <p className="text-navy-600/60 text-sm mb-8">所有標記 * 的欄位為必填項目</p>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-navy-800 mb-2">
                      <User size={14} className="inline mr-2 text-gold-500" />
                      聯絡人 *
                    </label>
                    <input
                      type="text"
                      name="contactPerson"
                      value={form.contactPerson}
                      onChange={handleChange}
                      required
                      placeholder="姓名"
                      className="w-full px-4 py-3 border border-cream-300 bg-cream-50 text-navy-900 placeholder:text-navy-400/50 focus:border-gold-400 focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy-800 mb-2">
                      <Phone size={14} className="inline mr-2 text-gold-500" />
                      聯絡電話 *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      placeholder="+852 XXXX XXXX"
                      className="w-full px-4 py-3 border border-cream-300 bg-cream-50 text-navy-900 placeholder:text-navy-400/50 focus:border-gold-400 focus:outline-none transition-colors"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-navy-800 mb-2">
                      電郵地址
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="example@company.com"
                      className="w-full px-4 py-3 border border-cream-300 bg-cream-50 text-navy-900 placeholder:text-navy-400/50 focus:border-gold-400 focus:outline-none transition-colors"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-navy-800 mb-2">
                      <FileText size={14} className="inline mr-2 text-gold-500" />
                      搬遷類型 *
                    </label>
                    <select
                      name="relocationType"
                      value={form.relocationType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-cream-300 bg-cream-50 text-navy-900 focus:border-gold-400 focus:outline-none transition-colors"
                    >
                      <option value="">請選擇搬遷類型</option>
                      {relocationTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy-800 mb-2">
                      <Calendar size={14} className="inline mr-2 text-gold-500" />
                      期望搬運日期 *
                    </label>
                    <input
                      type="date"
                      name="preferredDate"
                      value={form.preferredDate}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-cream-300 bg-cream-50 text-navy-900 focus:border-gold-400 focus:outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy-800 mb-2">
                      <Clock size={14} className="inline mr-2 text-gold-500" />
                      期望時段 *
                    </label>
                    <select
                      name="preferredTime"
                      value={form.preferredTime}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-cream-300 bg-cream-50 text-navy-900 focus:border-gold-400 focus:outline-none transition-colors"
                    >
                      <option value="">請選擇時段</option>
                      {timeSlots.map((slot) => (
                        <option key={slot} value={slot}>{slot}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-navy-800 mb-2">
                      補充說明（如有需搬運之設備及實景圖片，請提供）
                    </label>
                    <textarea
                      name="notes"
                      value={form.notes}
                      onChange={handleChange}
                      rows={4}
                      placeholder="例：X光機一部、牙科椅兩張，三樓無電梯..."
                      className="w-full px-4 py-3 border border-cream-300 bg-cream-50 text-navy-900 placeholder:text-navy-400/50 focus:border-gold-400 focus:outline-none transition-colors resize-none"
                    />

                    <div className="mt-4">
                      <label className="flex items-center justify-center gap-2 w-full px-4 py-6 border-2 border-dashed border-cream-300 bg-cream-50 text-navy-600/70 text-sm cursor-pointer hover:border-gold-400 hover:text-gold-600 transition-colors">
                        <ImagePlus size={18} />
                        點擊上載設備或實景圖片（最多 5 張）
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageChange}
                          className="hidden"
                        />
                      </label>
                      {images.length > 0 && (
                        <div className="mt-3 grid grid-cols-5 gap-2">
                          {images.map((file, i) => (
                            <div key={i} className="relative group">
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="w-full h-16 object-cover border border-cream-300"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(i)}
                                className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-navy-900/30 border-t-navy-900 rounded-full animate-spin" />
                        提交中...
                      </span>
                    ) : (
                      <>
                        提交預約
                        <ArrowRight className="ml-2" size={18} />
                      </>
                    )}
                  </button>
                  <span className="text-xs text-navy-600/50">
                    提交後將自動開啟 WhatsApp（5580 9426）傳送預約資料
                  </span>
                </div>
              </form>
            </div>

            <div className="space-y-6">
              <div className="bg-white border border-cream-300 p-8">
                <h4 className="font-serif text-lg text-navy-900 mb-6">免費評估包含</h4>
                <ul className="space-y-4">
                  {[
                    '現場設備規格記錄',
                    '搬遷路線實地勘察',
                    '包裝方案建議',
                    '詳細報價單',
                    '時間表規劃',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-navy-700/80">
                      <CheckCircle2 size={16} className="text-gold-500 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white border border-cream-300 p-8">
                <h4 className="font-serif text-lg text-navy-900 mb-4">即時聯絡</h4>
                <div className="space-y-3">
                  <a
                    href={`tel:${cms.site.phone.replace(/\s/g, '')}`}
                    className="flex items-center gap-3 text-sm text-navy-700 hover:text-gold-600 transition-colors"
                  >
                    <Phone size={16} className="text-gold-500" />
                    {cms.site.phone}
                  </a>
                  <a
                    href={`https://wa.me/${cms.site.whatsappNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-sm text-navy-700 hover:text-gold-600 transition-colors"
                  >
                    <MessageCircle size={16} className="text-gold-500" />
                    WhatsApp：5580 9426
                  </a>
                  <div className="flex items-center gap-3 text-sm text-navy-700">
                    <Clock size={16} className="text-gold-500" />
                    {cms.site.businessHours}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
